/**
 * New paper (design v2 screen 4): the ready book's chapters as checkboxes — a long list shows
 * three and "Show all N chapters", with All and None beside the legend — three length tiles,
 * "Make a paper", and what it costs beneath. Only a chapter with flashcards can be ticked; the
 * rest follow, disabled. The click lands visibly: the button is busy while the request is in
 * flight, then a confirmation replaces the cost line, and a failure shows here, not elsewhere.
 */
import { useRef, useState } from "react";
import type { PaperLength } from "../api/api";
import { chapterTitle } from "./chapters";

export type RequestResult = { ok: true } | { ok: false; message: string };

type Props = {
  /** `made: false` is a chapter with no flashcards yet (left out, or not yet made). */
  chapters: { number: number; title: string; made?: boolean }[];
  /** The confirmed format's total; 0 until there is one. */
  fullMarks: number;
  /** The confirmed format's time allowed; 0 when unknown. */
  fullMinutes?: number;
  remaining: number;
  limit: number;
  disabledReason: string | null;
  onRequest: (body: { chapters: number[]; length: PaperLength }) => Promise<RequestResult>;
  /** "Papers" in the confirmation: bring the papers list into view. */
  onShowPapers?: () => void;
};

const SHOWN = 3;

function duration(minutes: number): string {
  if (minutes <= 0) return "";
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

export function NewPaperForm({
  chapters: given,
  fullMarks,
  fullMinutes = 0,
  remaining,
  disabledReason,
  onRequest,
  onShowPapers,
}: Props) {
  // Made chapters first; the others after them, never tickable.
  const made = given.filter((c) => c.made !== false);
  const unmade = given.filter((c) => c.made === false);
  const chapters = [...made, ...unmade];
  const [ticked, setTicked] = useState<Set<number>>(() => new Set(made.map((c) => c.number)));
  const [length, setLength] = useState<PaperLength>("short");
  const [all, setAll] = useState(chapters.length <= SHOWN + 1);
  const [busy, setBusy] = useState(false);
  // State alone lets clicks in one tick through before the re-render disables the button.
  const inFlight = useRef(false);
  const [sent, setSent] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  // Touching the form again brings back the cost line in place of the confirmation.
  const touch = () => {
    setSent(false);
    setFailure(null);
  };
  const toggle = (n: number) => {
    touch();
    setTicked((s) => {
      const next = new Set(s);
      if (next.has(n)) next.delete(n);
      else next.add(n);
      return next;
    });
  };
  const full = fullMarks > 0 ? String(fullMarks) : "?";
  const tiles: { value: PaperLength; name: string; marks: string; time: string; off: boolean }[] = [
    { value: "short", name: "Short", marks: "25", time: "30 min", off: false },
    { value: "standard", name: "Standard", marks: "40", time: "45 min", off: false },
    { value: "full", name: "Full", marks: full, time: duration(fullMinutes), off: fullMarks <= 0 },
  ];
  const reason = disabledReason ?? (ticked.size === 0 ? "Tick at least one chapter." : null);
  const shown = all ? chapters : chapters.slice(0, SHOWN);

  async function make() {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setFailure(null);
    setSent(false);
    try {
      const r = await onRequest({ chapters: [...ticked].sort((a, b) => a - b), length });
      if (r.ok) setSent(true);
      else if (r.message) setFailure(r.message);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }

  return (
    <section className="panel new-paper" aria-label="New paper">
      <h2>New paper</h2>
      <fieldset className="new-paper__chapters">
        <legend className="new-paper__legend">
          <span>
            Chapters
            {unmade.length > 0 && ` · ${made.length} with flashcards`}
          </span>
          <span className="new-paper__bulk">
            <button
              type="button"
              className="new-paper__all"
              disabled={ticked.size === made.length}
              onClick={() => {
                touch();
                setTicked(new Set(made.map((c) => c.number)));
              }}
            >
              All
            </button>
            <button
              type="button"
              className="new-paper__all"
              disabled={ticked.size === 0}
              onClick={() => {
                touch();
                setTicked(new Set());
              }}
            >
              None
            </button>
          </span>
        </legend>
        {shown.map((c) => {
          const off = c.made === false;
          return (
            <label
              key={c.number}
              className={`new-paper__chapter${off ? " new-paper__chapter--off" : ""}`}
            >
              <input
                type="checkbox"
                checked={ticked.has(c.number)}
                disabled={off}
                onChange={() => toggle(c.number)}
                aria-label={`${c.number}. ${chapterTitle(c.title)}`}
              />
              <span>
                {c.number}. {chapterTitle(c.title)}
                {off && <span className="muted"> · no flashcards yet</span>}
              </span>
            </label>
          );
        })}
        {!all && (
          <button type="button" className="new-paper__all" onClick={() => setAll(true)}>
            Show all {chapters.length} chapters
            <span className="muted"> · {ticked.size} ticked</span>
          </button>
        )}
      </fieldset>
      <fieldset className="new-paper__length">
        <legend>Length</legend>
        <div className="length-tiles">
          {tiles.map((t) => (
            <label
              key={t.value}
              className={`length-tile${length === t.value ? " length-tile--on" : ""}${
                t.off ? " length-tile--off" : ""
              }`}
            >
              <input
                type="radio"
                name="length"
                value={t.value}
                disabled={t.off}
                checked={length === t.value}
                onChange={() => {
                  touch();
                  setLength(t.value);
                }}
                aria-label={`${t.name} paper · ${t.marks} marks${t.time ? ` · ${t.time}` : ""}`}
              />
              <span className="length-tile__name">{t.name}</span>
              <span className="length-tile__marks num">{t.marks}</span>
              <span className="length-tile__unit">marks{t.time ? ` · ${t.time}` : ""}</span>
            </label>
          ))}
        </div>
      </fieldset>
      {reason && <p className="new-paper__reason">{reason}</p>}
      <button
        className="button button--primary new-paper__make"
        disabled={reason !== null || busy}
        onClick={() => void make()}
      >
        {busy ? "Making your paper…" : "Make a paper"}
      </button>
      {failure && (
        <p role="alert" className="field-error new-paper__failure">
          {failure}
        </p>
      )}
      <div aria-live="polite">
        {sent ? (
          <p className="new-paper__sent">
            Your paper is on its way. It takes a few minutes. You'll find it under{" "}
            <button type="button" className="subject__link" onClick={() => onShowPapers?.()}>
              Papers
            </button>
            .
          </p>
        ) : (
          reason === null && (
            <p className="new-paper__cost num">
              Uses 1 of your {remaining} remaining paper{remaining === 1 ? "" : "s"} this month
            </p>
          )
        )}
      </div>
    </section>
  );
}
